/**
 * @file     htmlHeadings.ts
 * @purpose  Table of contents for hand-written posts: finds the h2s in the
 *           stored body HTML, gives each a stable anchor id (keeping one the
 *           author already set), and returns the rewritten HTML with the list
 *           the sticky contents rail links to. Pure, so the server render and
 *           the client hydrate produce the same ids.
 */
import type { HeadingEntry } from '../blocks/ContentBlocks';

const H2 = /<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/gi;
const ID_ATTR = /\sid\s*=\s*(["'])(.*?)\1/i;

const decodeEntities = (text: string) =>
  text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&rsquo;|&lsquo;/g, '’');

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);

export function anchorHtmlHeadings(html: string): { html: string; headings: HeadingEntry[] } {
  const headings: HeadingEntry[] = [];
  const used = new Set<string>();
  let index = 0;
  const rewritten = html.replace(H2, (match, attrs: string | undefined, inner: string) => {
    const text = decodeEntities(inner.replace(/<[^>]+>/g, '')).replace(/\s+/g, ' ').trim();
    if (!text) return match;
    const existing = attrs?.match(ID_ATTR)?.[2];
    let id = existing || `h-${index}-${slugify(text) || 'section'}`;
    while (used.has(id)) id = `${id}-${index}`;
    used.add(id);
    index += 1;
    headings.push({ id, text, level: 2 });
    if (existing) return match;
    return `<h2 id="${id}"${attrs ?? ''}>${inner}</h2>`;
  });
  return { html: rewritten, headings };
}
